import { useCallback, useEffect, useState } from "react";
import { KeyRound, Plus, ShieldCheck, UserCog } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api-client";
import { useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type { ProjectSummary, UserRow } from "@/lib/domain";

const ROLES = ["admin", "sales", "installer", "architect", "client"] as const;
const LOCALES = ["it", "en", "es"] as const;

const TEXT = {
  it: {
    kicker: "Amministrazione",
    title: "Utenti e permessi",
    intro:
      "Il ruolo dice chi è una persona; l'accesso ai cantieri si concede per singolo progetto. Un architetto senza concessione non vede nulla, anche se è approvato.",
    newUser: "Nuovo utente",
    name: "Nome",
    email: "Email",
    password: "Password temporanea",
    passwordHint: "L'utente dovrà cambiarla al primo accesso.",
    role: "Ruolo",
    locale: "Lingua",
    company: "Azienda / Studio",
    create: "Crea utente",
    creating: "Creazione…",
    projects: "Cantieri",
    grants: "Accessi ai cantieri",
    noAccess: "Nessun accesso",
    viewer: "Lettura",
    editor: "Modifica",
    none: "Nessuno",
    mustChange: "password temporanea",
    suspend: "Sospendi",
    activate: "Riattiva",
    reset: "Reimposta password",
    resetPrompt: "Nuova password temporanea (min 8 caratteri)",
    noProjects: "Nessun cantiere disponibile.",
    saved: "Salvato",
  },
  en: {
    kicker: "Administration",
    title: "Users and permissions",
    intro:
      "The role says who someone is; project access is granted one project at a time. An architect with no grant sees nothing, even once approved.",
    newUser: "New user",
    name: "Name",
    email: "Email",
    password: "Temporary password",
    passwordHint: "The user must change it at first sign-in.",
    role: "Role",
    locale: "Language",
    company: "Company / Studio",
    create: "Create user",
    creating: "Creating…",
    projects: "Projects",
    grants: "Project access",
    noAccess: "No access",
    viewer: "View",
    editor: "Edit",
    none: "None",
    mustChange: "temporary password",
    suspend: "Suspend",
    activate: "Reactivate",
    reset: "Reset password",
    resetPrompt: "New temporary password (min 8 characters)",
    noProjects: "No projects available.",
    saved: "Saved",
  },
  es: {
    kicker: "Administración",
    title: "Usuarios y permisos",
    intro:
      "El rol dice quién es alguien; el acceso a las obras se concede obra por obra. Un arquitecto sin concesión no ve nada, aunque esté aprobado.",
    newUser: "Nuevo usuario",
    name: "Nombre",
    email: "Email",
    password: "Contraseña temporal",
    passwordHint: "El usuario deberá cambiarla en el primer acceso.",
    role: "Rol",
    locale: "Idioma",
    company: "Empresa / Estudio",
    create: "Crear usuario",
    creating: "Creando…",
    projects: "Obras",
    grants: "Accesos a obras",
    noAccess: "Sin acceso",
    viewer: "Lectura",
    editor: "Edición",
    none: "Ninguno",
    mustChange: "contraseña temporal",
    suspend: "Suspender",
    activate: "Reactivar",
    reset: "Restablecer contraseña",
    resetPrompt: "Nueva contraseña temporal (mín. 8 caracteres)",
    noProjects: "No hay obras disponibles.",
    saved: "Guardado",
  },
} as const;

type Tx = Record<keyof (typeof TEXT)["it"], string>;

export default function UsersPage() {
  const { locale } = useLocale();
  const tx = TEXT[locale];
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [adding, setAdding] = useState(false);
  const [openGrants, setOpenGrants] = useState<string | null>(null);
  const [grants, setGrants] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([api.users(), api.projects()]);
      setRows(u);
      setProjects(p);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function openGrantEditor(userId: string) {
    if (openGrants === userId) return setOpenGrants(null);
    setOpenGrants(userId);
    try {
      const mine = await api.users(); // ensure fresh directory
      setRows(mine);
      const membership = await api.userMemberships(userId);
      setGrants(
        Object.fromEntries(membership.map((m) => [m.projectId, m.access])),
      );
    } catch {
      setGrants({});
    }
  }

  async function setGrant(userId: string, projectId: string, access: string) {
    const previous = grants[projectId] ?? "none";
    setGrants((g) => ({ ...g, [projectId]: access }));
    try {
      if (access === "none") {
        await api.revokeProjectMember(projectId, userId);
      } else {
        await api.grantProject(projectId, userId, access);
      }
      await load();
    } catch {
      setGrants((g) => ({ ...g, [projectId]: previous }));
    }
  }

  return (
    <AppShell>
      <div className="brand-kicker">{tx.kicker}</div>
      <h1 className="font-serif text-2xl text-paper sm:text-3xl">{tx.title}</h1>
      <p className="mt-2 max-w-3xl text-[0.78rem] leading-relaxed text-ice/65">
        {tx.intro}
      </p>

      <button
        type="button"
        onClick={() => setAdding((v) => !v)}
        className="mt-5 rounded-full border border-white/15 px-3 py-1.5 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ice/70"
      >
        + {tx.newUser}
      </button>

      {error && <p className="mt-3 text-xs text-pink">{error}</p>}

      {adding && (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const f = e.currentTarget;
            const data = new FormData(f);
            setBusy(true);
            setError(null);
            try {
              await api.createUser({
                name: String(data.get("name") ?? ""),
                email: String(data.get("email") ?? ""),
                password: String(data.get("password") ?? ""),
                role: String(data.get("role") ?? "client"),
                locale: String(data.get("locale") ?? "it"),
                companyName: String(data.get("companyName") ?? "") || undefined,
              });
              f.reset();
              setAdding(false);
              await load();
            } catch (err) {
              setError(err instanceof Error ? err.message : "error");
            } finally {
              setBusy(false);
            }
          }}
          className="glass-panel-strong mt-3 grid gap-3 p-5 sm:grid-cols-3"
        >
          <label className="block">
            <span className="field-label">{tx.name}</span>
            <input name="name" className="field" required />
          </label>
          <label className="block">
            <span className="field-label">{tx.email}</span>
            <input name="email" type="email" className="field" required />
          </label>
          <label className="block">
            <span className="field-label">{tx.password}</span>
            <input name="password" className="field" minLength={8} required />
          </label>
          <label className="block">
            <span className="field-label">{tx.role}</span>
            <select name="role" className="field" defaultValue="architect">
              {ROLES.map((r) => (
                <option key={r} value={r} className="bg-night">
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="field-label">{tx.company}</span>
            <input name="companyName" className="field" />
          </label>
          <label className="block">
            <span className="field-label">{tx.locale}</span>
            <select name="locale" className="field" defaultValue="it">
              {LOCALES.map((l) => (
                <option key={l} value={l} className="bg-night">
                  {l}
                </option>
              ))}
            </select>
          </label>
          <p className="text-[0.68rem] text-ice/50 sm:col-span-3">
            {tx.passwordHint}
          </p>
          <div className="sm:col-span-3">
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-magenta px-5 py-2 text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-paper disabled:opacity-50"
            >
              <Plus className="h-3 w-3" />
              {busy ? tx.creating : tx.create}
            </button>
          </div>
        </form>
      )}

      <div className="mt-4 grid gap-3">
        {rows === null && <p className="text-sm text-ice/60">…</p>}
        {rows?.map((u) => (
          <article key={u.id} className={cn("glass-panel-strong p-4", !u.active && "opacity-60")}>
            <div className="flex flex-wrap items-start gap-3">
              <UserCog className="mt-0.5 h-4 w-4 shrink-0 text-goldbright" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-serif text-[0.98rem] text-paper">{u.name}</span>
                  <span className="rounded-full border border-white/15 px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.14em] text-goldbright">
                    {u.role}
                  </span>
                  {u.status !== "active" && (
                    <span className="text-[0.58rem] uppercase tracking-[0.12em] text-pink">
                      {u.status}
                    </span>
                  )}
                  {u.mustChangePassword && (
                    <span className="inline-flex items-center gap-1 text-[0.58rem] uppercase tracking-[0.12em] text-ice/55">
                      <KeyRound className="h-3 w-3" />
                      {tx.mustChange}
                    </span>
                  )}
                </div>
                <div className="text-[0.7rem] text-ice/60">
                  {u.email}
                  {u.installer?.companyName ? ` · ${u.installer.companyName}` : ""}
                  {u.architect?.studioName ? ` · ${u.architect.studioName}` : ""}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="field !w-auto !py-1.5 text-[0.62rem]"
                  value={u.role}
                  onChange={async (e) => {
                    await api.updateUser(u.id, { role: e.target.value });
                    await load();
                  }}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r} className="bg-night">
                      {r}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={async () => {
                    await api.updateUser(u.id, {
                      status: u.status === "suspended" ? "active" : "suspended",
                    });
                    await load();
                  }}
                  className="rounded-full border border-white/15 px-3 py-1.5 text-[0.56rem] font-semibold uppercase tracking-[0.12em] text-ice/70"
                >
                  {u.status === "suspended" ? tx.activate : tx.suspend}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const pw = window.prompt(tx.resetPrompt);
                    if (pw && pw.length >= 8) {
                      await api.resetUserPassword(u.id, pw);
                      await load();
                    }
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[0.56rem] font-semibold uppercase tracking-[0.12em] text-ice/70"
                >
                  <KeyRound className="h-3 w-3" />
                  {tx.reset}
                </button>
                <button
                  type="button"
                  onClick={() => openGrantEditor(u.id)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-3 py-1.5 text-[0.56rem] font-semibold uppercase tracking-[0.12em] text-goldbright"
                >
                  <ShieldCheck className="h-3 w-3" />
                  {tx.grants}
                  {u._count?.memberships ? ` (${u._count.memberships})` : ""}
                </button>
              </div>
            </div>

            {openGrants === u.id && (
              <div className="mt-4 border-t border-white/10 pt-4">
                <div className="brand-kicker">{tx.grants}</div>
                {projects.length === 0 ? (
                  <p className="mt-2 text-[0.72rem] text-ice/55">{tx.noProjects}</p>
                ) : (
                  <div className="mt-2 grid gap-1.5">
                    {projects.map((p) => (
                      <div
                        key={p.id}
                        className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                      >
                        <span className="min-w-0 flex-1 text-[0.76rem] text-paper/90">
                          {p.name}
                          <span className="ml-2 text-[0.62rem] text-ice/45">
                            {p.country}
                          </span>
                        </span>
                        <select
                          className="field !w-auto !py-1 text-[0.62rem]"
                          value={grants[p.id] ?? "none"}
                          onChange={(e) => setGrant(u.id, p.id, e.target.value)}
                        >
                          <option value="none" className="bg-night">
                            {tx.none}
                          </option>
                          <option value="viewer" className="bg-night">
                            {tx.viewer}
                          </option>
                          <option value="editor" className="bg-night">
                            {tx.editor}
                          </option>
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
    </AppShell>
  );
}

export type { Tx };
